import { useEffect, useRef, useCallback } from 'preact/hooks';
import {
  allMessages,
  isLoadingMessages,
  hasMoreMessages,
  nextCursor,
  conversationId,
  token,
  setMessages,
  mergeMessages,
} from '../store/state';
import { MessageBubble } from './MessageBubble';
import { ApiError } from '../api/client';
import type { WidgetApiClient } from '../api/client';
import type { OptimisticMessage } from '../types';

interface MessageListProps {
  apiClient: WidgetApiClient | null;
  isActive: boolean;
}

/**
 * Adaptive polling intervals
 */
const POLL_INTERVAL_ACTIVE = 2000; // 2s when user is active
const POLL_INTERVAL_IDLE = 10000; // 10s when idle
const POLL_INTERVAL_BACKGROUND = 30000; // 30s when in background
const IDLE_TIMEOUT = 30000; // 30s of no activity = idle
const STALE_THRESHOLD_MS = 60000; // 1 minute - force refresh if hidden longer

export function MessageList({ apiClient, isActive }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(Date.now());
  const isInitialLoadRef = useRef(true);
  const isVisibleRef = useRef(!document.hidden);
  const lastPollRef = useRef(Date.now());
  const consecutiveErrorsRef = useRef(0);
  const lastMessageTimestampRef = useRef<string | null>(null);

  // Get current polling interval based on activity, visibility, and errors
  const getPollInterval = useCallback(() => {
    let base: number;
    // If tab is hidden, always use background interval
    if (!isVisibleRef.current) base = POLL_INTERVAL_BACKGROUND;
    else if (!isActive) base = POLL_INTERVAL_BACKGROUND;
    else if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT) base = POLL_INTERVAL_IDLE;
    else base = POLL_INTERVAL_ACTIVE;

    // Apply exponential backoff on consecutive errors (cap at 8x)
    if (consecutiveErrorsRef.current > 0) {
      const multiplier = Math.min(Math.pow(2, consecutiveErrorsRef.current), 8);
      return Math.min(base * multiplier, POLL_INTERVAL_BACKGROUND);
    }
    return base;
  }, [isActive]);

  // Fetch messages (for initial load or pagination with cursor)
  const fetchMessages = useCallback(async (cursor?: string) => {
    if (!apiClient || !conversationId.value || !token.value) return;

    try {
      isLoadingMessages.value = true;
      lastPollRef.current = Date.now();

      const params: { conversationId: string; limit: number; cursor?: string } = {
        conversationId: conversationId.value,
        limit: 50,
      };
      if (cursor) {
        params.cursor = cursor;
      }
      const response = await apiClient.getMessages(params);

      if (cursor) {
        // Append older messages
        mergeMessages(response.messages);
      } else {
        // Initial load
        setMessages(
          response.messages,
          response.pagination.has_more,
          response.pagination.next_cursor ?? null
        );
        isInitialLoadRef.current = false;

        // Track last message timestamp for subsequent polls
        if (response.messages.length > 0) {
          const last = response.messages[response.messages.length - 1]!;
          lastMessageTimestampRef.current = last.created_at;
        }
      }
      consecutiveErrorsRef.current = 0;
    } catch (err) {
      if (err instanceof ApiError && err.isAuthError()) {
        console.warn('[EasyHelp] Auth error during fetch, token refresh will handle it');
      } else {
        console.error('[EasyHelp] Failed to fetch messages:', err);
        consecutiveErrorsRef.current++;
      }
    } finally {
      isLoadingMessages.value = false;
    }
  }, [apiClient]);

  // Poll for new messages only (uses `since` to get only new messages)
  const pollNewMessages = useCallback(async () => {
    if (!apiClient || !conversationId.value || !token.value) return;

    try {
      lastPollRef.current = Date.now();

      const params: { conversationId: string; limit: number; since?: string } = {
        conversationId: conversationId.value,
        limit: 50,
      };
      if (lastMessageTimestampRef.current) {
        params.since = lastMessageTimestampRef.current;
      }

      const response = await apiClient.getMessages(params);

      if (response.messages.length > 0) {
        mergeMessages(response.messages);
        // Update last timestamp
        const last = response.messages[response.messages.length - 1]!;
        lastMessageTimestampRef.current = last.created_at;
      }
      consecutiveErrorsRef.current = 0;
    } catch (err) {
      if (err instanceof ApiError && err.isAuthError()) {
        console.warn('[EasyHelp] Auth error during polling, token refresh will handle it');
      } else {
        console.error('[EasyHelp] Failed to poll messages:', err);
        consecutiveErrorsRef.current++;
      }
    }
  }, [apiClient]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Start polling with setTimeout (no interval leak)
  const startPolling = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const scheduleNext = async () => {
      await pollNewMessages();
      // Schedule next poll only if not cancelled
      timeoutRef.current = setTimeout(scheduleNext, getPollInterval());
    };

    timeoutRef.current = setTimeout(scheduleNext, getPollInterval());
  }, [pollNewMessages, getPollInterval]);

  // Track user activity
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Initial fetch and polling setup
  useEffect(() => {
    if (!isActive || !token.value || !conversationId.value) {
      stopPolling();
      return;
    }

    // Reset state on fresh token / conversation change
    consecutiveErrorsRef.current = 0;
    isInitialLoadRef.current = true;
    lastMessageTimestampRef.current = null;

    // Initial fetch
    fetchMessages();

    // Start polling
    startPolling();

    // Visibility change handler - pause polling when tab hidden
    const handleVisibilityChange = () => {
      const wasHidden = !isVisibleRef.current;
      isVisibleRef.current = !document.hidden;

      if (isVisibleRef.current && wasHidden) {
        // Tab became visible - fetch new messages if stale and restart polling
        const timeSinceLastPoll = Date.now() - lastPollRef.current;
        if (timeSinceLastPoll > STALE_THRESHOLD_MS) {
          pollNewMessages();
        }
        // Reset activity and restart polling with active interval
        lastActivityRef.current = Date.now();
        startPolling();
      } else if (!isVisibleRef.current) {
        // Tab became hidden - restart with background interval
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track activity
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', trackActivity);
      container.addEventListener('click', trackActivity);
    }

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (container) {
        container.removeEventListener('scroll', trackActivity);
        container.removeEventListener('click', trackActivity);
      }
    };
  }, [isActive, token.value, conversationId.value, fetchMessages, pollNewMessages, startPolling, stopPolling, trackActivity]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current && allMessages.value.length > 0) {
      // Only auto-scroll if user is near bottom
      const container = containerRef.current;
      if (container) {
        const isNearBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [allMessages.value.length]);

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (nextCursor.value) {
      fetchMessages(nextCursor.value);
    }
  }, [fetchMessages]);

  // Empty state
  if (!token.value) {
    return (
      <div class="easyhelp-messages">
        <div class="easyhelp-loading">
          <div class="easyhelp-loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  if (allMessages.value.length === 0 && !isLoadingMessages.value) {
    return (
      <div class="easyhelp-messages">
        <div class="easyhelp-empty">
          <svg class="easyhelp-empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>No messages yet.</span>
          <span>Start the conversation!</span>
        </div>
      </div>
    );
  }

  return (
    <div class="easyhelp-messages" ref={containerRef}>
      {/* Load more button */}
      {hasMoreMessages.value && (
        <div class="easyhelp-load-more">
          <button onClick={handleLoadMore} disabled={isLoadingMessages.value}>
            {isLoadingMessages.value ? 'Loading...' : 'Load older messages'}
          </button>
        </div>
      )}

      {/* Messages */}
      {allMessages.value.map((message: OptimisticMessage) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {/* Loading indicator */}
      {isLoadingMessages.value && allMessages.value.length === 0 && (
        <div class="easyhelp-loading">
          <div class="easyhelp-loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
}
